
import React from 'react';
import NavigationBar from '@/components/landing/NavigationBar';
import Footer from '@/components/landing/Footer';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const Reports = () => {
  return (
    <>
      <Helmet>
        <title>Market Intelligence Reports | AnalystX</title>
        <meta name="description" content="Browse our catalog of AI-generated market intelligence reports covering industry trends, vendor competition, and supplier analysis." />
        <meta name="keywords" content="market intelligence reports, market research, vendor competition, supplier analysis, competitive intelligence" />
      </Helmet>
      
      <div className="min-h-screen bg-gray-900 text-white">
        <NavigationBar />
        
        <main className="container mx-auto px-4 py-16">
          <h1 className="text-4xl font-bold mb-6">Market Intelligence Reports</h1>
          
          <div className="bg-gray-800/50 rounded-xl p-6 mb-12">
            <h2 className="text-2xl font-semibold mb-4">Comprehensive Market Insights</h2>
            <p className="text-gray-300 mb-4">
              AnalystX generates detailed market intelligence reports that provide deep insights into industries, sectors, and competitive landscapes. Our AI-powered platform analyzes massive amounts of data to deliver actionable intelligence that helps businesses make informed decisions.
            </p>
            <p className="text-gray-300 mb-4">
              Each report includes market trends, growth projections, competitive analysis, and strategic recommendations tailored to your specific needs.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-gray-800/30 rounded-xl p-6 flex flex-col">
              <h3 className="text-xl font-semibold mb-3">Industry Analysis Reports</h3>
              <p className="text-gray-300 mb-4 flex-grow">
                Comprehensive assessments of specific industries, including market size, growth trends, key players, regulatory factors, and future outlook.
              </p>
              <Button variant="outline" className="w-full mt-4">
                <Link to="/auth" className="w-full">View Sample Report</Link>
              </Button>
            </div>
            
            <div className="bg-gray-800/30 rounded-xl p-6 flex flex-col">
              <h3 className="text-xl font-semibold mb-3">Vendor Competition Reports</h3>
              <p className="text-gray-300 mb-4 flex-grow">
                Detailed analysis of competitive landscapes, comparing vendors across multiple dimensions including products, pricing, market share, and customer satisfaction.
              </p>
              <Button variant="outline" className="w-full mt-4">
                <Link to="/auth" className="w-full">View Sample Report</Link>
              </Button>
            </div>
            
            <div className="bg-gray-800/30 rounded-xl p-6 flex flex-col">
              <h3 className="text-xl font-semibold mb-3">Supplier Analysis Reports</h3>
              <p className="text-gray-300 mb-4 flex-grow">
                Evaluations of supplier capabilities, reliability, pricing, geographic coverage, and strategic positioning within their respective markets.
              </p>
              <Button variant="outline" className="w-full mt-4">
                <Link to="/auth" className="w-full">View Sample Report</Link>
              </Button>
            </div>
          </div>
          
          <div className="bg-blue-900/30 rounded-xl p-6 border border-blue-800/50 mb-12">
            <h2 className="text-2xl font-semibold mb-4">Report Components</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-medium mb-2">Market Overview</h3>
                <p className="text-gray-300">Comprehensive assessment of market size, growth trends, and key drivers.</p>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-2">Competitive Landscape</h3>
                <p className="text-gray-300">Detailed analysis of market players, their offerings, and competitive positioning.</p>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-2">SWOT Analysis</h3>
                <p className="text-gray-300">Evaluation of strengths, weaknesses, opportunities, and threats for key market participants.</p>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-2">Strategic Recommendations</h3>
                <p className="text-gray-300">Actionable insights and strategic guidance based on market intelligence findings.</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-800/50 rounded-xl p-6 text-center">
            <h2 className="text-2xl font-semibold mb-4">Get Started with Custom Reports</h2>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Need specific market intelligence on vendor competition or supplier analysis? AnalystX can generate custom reports tailored to your exact requirements.
            </p>
            <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
              <Link to="/auth" className="w-full">Create Custom Report</Link>
            </Button>
          </div>
        </main>
        
        <Footer />
      </div>
    </>
  );
};

export default Reports;
